
import { IonButton } from '@ionic/react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import React, { useState } from 'react';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { ActivePatientEditForm } from './ActivePatientEditForm';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { PatientFormState } from '../../avatar/PatientFormState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { useSubscription } from '@meraki-internal/state';
import { useConfirmLinkThenSave } from '../../caremap/useConfirmLinkThenSave';

export class EditPatientDrawer implements IQueryParamDrivenDrawerContentProvider {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper){}

    mode = 'edit-patient';

    Component = EditPatientDrawerView;

    open = () => {
        this.drawerHelper.open({ mode: this.mode });
    };
}

const EditPatientDrawerView: React.FC = () => {
    const careMapStateInstance = useInstance(CareMapState);
    const patientForm = useInstance(PatientFormState);
    const tracking = useInstance(MixPanelEventEmitter);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const confirmLinkThenSave = useConfirmLinkThenSave();

    // TODO: consider moving to PatientFormState
    const [initialDiagnosesPrivacy] = useState<boolean>(careMapStateInstance.getActiveCaremap()?.patient.diagnosesIsPublic!);

    useSubscription(() => patientForm);
    useSubscription(() => careMapStateInstance);

    const patient = patientForm.state.patient;

    const isValid = Boolean(patient?.firstName?.trim());

    const actuallySave = () => {
        if (!patient){
            throw new Error(`attempted to save but there is no patient`);
        }
        careMapStateInstance.updatePatient(patient);

        // TODO: DRY
        tracking.track('Care Recipient Edited', () => ({
            '# of Diagnoses': (patient.diagnoses || []).length,
            '# of Allergies': (patient.allergies || []).length,
            'Diagnoses Privacy': patient.diagnosesIsPublic ? 'public' : 'private',
            'Diagnoses Privacy Toggled': patient.diagnosesIsPublic !== initialDiagnosesPrivacy,
            '# of Contact Methods': careMapStateInstance.cleanupContactMethods(patient.contactMethods).length
        }));

        drawerHelper.close();
    };

    const onSave = () => {
        confirmLinkThenSave({
            linkedUserId: patient?.linkedUserId,
            email: patient ? careMapStateInstance.getContactMethodOrDefault(patient, 'email').value : '',
            name: patient?.firstName,
            isSelf: patient ? careMapStateInstance.isMyMember(patient) : false,
            memberNoun: 'care recipient',
            save: actuallySave
        });
    };

    const onCancel = () => {
        drawerHelper.close();
    };

    const buttons = <>
        <IonButton data-type="save-button" disabled={!isValid} onClick={onSave}>Save</IonButton>
        <IonButton data-type="cancel-button" fill="outline" onClick={onCancel}>Cancel</IonButton>
    </>;

    const title = careMapStateInstance.isUserThePatient()
        ? 'Edit Your Info'
        : 'Edit Care Recipient';

    return (
        <QueryParamDrivenDrawerPage
            title={title}
            hasBack={true}
            buttons={buttons}
            onClose={onCancel}
            data-type="member-edit"
            data-member-id="patient"
        >
            <ActivePatientEditForm />
        </QueryParamDrivenDrawerPage>
    );
};
